import React, { useState } from 'react';
import { ChartTypeKey, CandlestickColors, LineConfig } from '../../types';
import {
  DEFAULT_CANDLESTICK_COLORS,
  DEFAULT_LINE_CONFIG,
  DEFAULT_BAR_CONFIG,
} from '../../constants';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as CandlestickIcon } from '../../../../assets/icons/StockChart/calistic-icon.svg';
import { ReactComponent as LineIcon } from '../../../../assets/icons/StockChart/Union.svg';
import { ReactComponent as BarIcon } from '../../../../assets/icons/StockChart/Bars.svg';
import { ReactComponent as ResetIcon } from '../../../../assets/icons/StockChart/replay_24px.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import SettingsPopup from '../common/SettingsPopup';
import SettingsCheckbox from '../common/SettingsCheckbox';
import ColorSwatch from '../common/ColorSwatch';
import LineStyleEditor from '../common/LineStyleEditor';
import '../../../../Components/Common/typography.css';
import './ChartTypeSettings.css';

interface ChartTypeSettingsProps {
  selectedType: ChartTypeKey;
  candlestickColors: CandlestickColors;
  lineConfig: LineConfig;
  barConfig: LineConfig;
  onTypeChange: (type: ChartTypeKey) => void;
  onCandlestickColorsChange: (colors: CandlestickColors) => void;
  onLineConfigChange: (config: LineConfig) => void;
  onBarConfigChange: (config: LineConfig) => void;
}

const ChartTypeSettings: React.FC<ChartTypeSettingsProps> = ({
  selectedType,
  candlestickColors,
  lineConfig,
  barConfig,
  onTypeChange,
  onCandlestickColorsChange,
  onLineConfigChange,
  onBarConfigChange,
}) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    candlestick: selectedType === 'candlestick',
    line: selectedType === 'line',
    bar: selectedType === 'bar',
  });

  const toggleSection = (key: ChartTypeKey) => {
    if (selectedType === key) {
      setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    } else {
      handleSelect(key);
    }
  };

  const handleSelect = (key: ChartTypeKey) => {
    onTypeChange(key);
    setExpandedSections({
      candlestick: key === 'candlestick',
      line: key === 'line',
      bar: key === 'bar',
    });
  };

  const handleResetCandlestick = () => {
    onCandlestickColorsChange(DEFAULT_CANDLESTICK_COLORS);
  };

  const handleResetLine = () => {
    onLineConfigChange(DEFAULT_LINE_CONFIG);
  };

  const handleResetBar = () => {
    onBarConfigChange(DEFAULT_BAR_CONFIG);
  };

  return (
    <SettingsPopup width={344} className="chart-type-popup">
      {/* Candlestick */}
      <div className="chart-type-section">
        <div
          className={`chart-type-row${selectedType === 'candlestick' ? ' chart-type-row-active' : ''}`}
          onClick={() => handleSelect('candlestick')}
        >
          <SettingsCheckbox checked={selectedType === 'candlestick'} onChange={() => handleSelect('candlestick')} />
          <span className="chart-type-icon">
            <CandlestickIcon />
          </span>
          <span className="body-m-regular">Candlestick</span>
          <span className="chart-type-actions">
            {expandedSections.candlestick && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetCandlestick();
                }}
                sx={{
                  padding: '2px',
                  '& svg path': { fill: 'var(--accent-primary)' },
                }}
              >
                <ResetIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('candlestick');
              }}
              sx={{ padding: '2px' }}
              className={
                expandedSections.candlestick ? 'chart-type-arrow-up' : ''
              }
            >
              <ArrowDownIcon />
            </IconButton>
          </span>
        </div>

        {expandedSections.candlestick && (
          <div className="chart-type-content">
            <div className="chart-type-color-row">
              <span className="body-s-regular chart-type-color-label chart-type-color-label--paired">
                UP Color
              </span>
              <span className="body-s-regular chart-type-color-sub">Line</span>
              <ColorSwatch
                color={candlestickColors.upLine}
                onChange={(color) =>
                  onCandlestickColorsChange({
                    ...candlestickColors,
                    upLine: color,
                  })
                }
                size={24}
              />
              <span className="body-s-regular chart-type-color-sub">Body</span>
              <ColorSwatch
                color={candlestickColors.upBody}
                onChange={(color) =>
                  onCandlestickColorsChange({
                    ...candlestickColors,
                    upBody: color,
                  })
                }
                size={24}
              />
            </div>
            <div className="chart-type-color-row">
              <span className="body-s-regular chart-type-color-label chart-type-color-label--paired">
                DOWN Color
              </span>
              <span className="body-s-regular chart-type-color-sub">Line</span>
              <ColorSwatch
                color={candlestickColors.downLine}
                onChange={(color) =>
                  onCandlestickColorsChange({
                    ...candlestickColors,
                    downLine: color,
                  })
                }
                size={24}
              />
              <span className="body-s-regular chart-type-color-sub">Body</span>
              <ColorSwatch
                color={candlestickColors.downBody}
                onChange={(color) =>
                  onCandlestickColorsChange({
                    ...candlestickColors,
                    downBody: color,
                  })
                }
                size={24}
              />
            </div>
          </div>
        )}
      </div>

      {/* Line */}
      <div className="chart-type-section">
        <div
          className={`chart-type-row${selectedType === 'line' ? ' chart-type-row-active' : ''}`}
          onClick={() => handleSelect('line')}
        >
          <SettingsCheckbox checked={selectedType === 'line'} onChange={() => handleSelect('line')} />
          <span className="chart-type-icon">
            <LineIcon />
          </span>
          <span className="body-m-regular">Line</span>
          <span className="chart-type-actions">
            {expandedSections.line && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetLine();
                }}
                sx={{
                  padding: '2px',
                  '& svg path': { fill: 'var(--accent-primary)' },
                }}
              >
                <ResetIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('line');
              }}
              sx={{ padding: '2px' }}
              className={expandedSections.line ? 'chart-type-arrow-up' : ''}
            >
              <ArrowDownIcon />
            </IconButton>
          </span>
        </div>

        {expandedSections.line && (
          <LineStyleEditor
            color={lineConfig.color}
            thickness={lineConfig.thickness}
            style={lineConfig.style}
            onColorChange={(color) =>
              onLineConfigChange({ ...lineConfig, color })
            }
            onThicknessChange={(thickness) =>
              onLineConfigChange({ ...lineConfig, thickness })
            }
            onStyleChange={(style) =>
              onLineConfigChange({ ...lineConfig, style })
            }
          />
        )}
      </div>

      {/* Bar */}
      <div className="chart-type-section">
        <div
          className={`chart-type-row${selectedType === 'bar' ? ' chart-type-row-active' : ''}`}
          onClick={() => handleSelect('bar')}
        >
          <SettingsCheckbox checked={selectedType === 'bar'} onChange={() => handleSelect('bar')} />
          <span className="chart-type-icon">
            <BarIcon />
          </span>
          <span className="body-m-regular">Bar</span>
          <span className="chart-type-actions">
            {expandedSections.bar && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetBar();
                }}
                sx={{
                  padding: '2px',
                  '& svg path': { fill: 'var(--accent-primary)' },
                }}
              >
                <ResetIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('bar');
              }}
              sx={{ padding: '2px' }}
              className={expandedSections.bar ? 'chart-type-arrow-up' : ''}
            >
              <ArrowDownIcon />
            </IconButton>
          </span>
        </div>

        {expandedSections.bar && (
          <div className="chart-type-content">
            <div className="chart-type-color-row">
              <span className="body-m-regular chart-type-color-label">Color</span>
              <ColorSwatch
                color={barConfig.color}
                onChange={(color) => onBarConfigChange({ ...barConfig, color })}
              />
            </div>
          </div>
        )}
      </div>
    </SettingsPopup>
  );
};

export default ChartTypeSettings;
